import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import AuthContext from './authContext';
import { subscribeCountsInvalidate } from '../util/countInvalidationBus';

// CountContext is the single source of truth for sidebar count badges.
//
// Counts used to be refreshed via 30s HTTP polling per key. They are now
// push-driven via Socket.io: the backend emits `counts:invalidate { keys }`
// (initially on connect for every key the user has rights for, and on
// every relevant mutation thereafter). For each invalidated key we call
// the locally registered fetch callback to pull the new value via the
// existing per-key HTTP endpoint.
//
// API surface kept identical to the polling implementation so callers
// don't have to change:
//   - registerInterval(key, callback) — register a fetch callback for a key
//   - unregisterInterval(key)         — drop the fetch callback for a key
//   - refreshCount(key)               — manually re-run the registered callback
//   - setCount(key, value)            — directly write a count value
//   - counts                          — current values { [key]: number }
//
// `registerInterval` no longer starts a setInterval. It registers the
// callback, calls it once immediately (so first paint shows a value),
// and from then on the callback only runs when the server pushes an
// invalidate for that key.

const CountContext = React.createContext({
  counts: {},
  setCount: () => {},
  registerInterval: () => {},
  unregisterInterval: () => {},
  refreshCount: () => {},
});

const REFETCH_DEBOUNCE_MS = 150;

export const CountContextProvider = ({ children }) => {
  const [counts, setCounts] = useState({});
  const refCounters = useRef({}); // { [key]: number } — registration ref-count
  const fetchCallbacksRef = useRef({}); // { [key]: () => void }
  const pendingRefetchRef = useRef(new Set()); // keys queued for debounced refetch
  const refetchTimerRef = useRef(null);
  const authCtx = useContext(AuthContext);

  const setCount = useCallback((key, value) => {
    setCounts(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const runRegisteredFetch = useCallback(key => {
    const cb = fetchCallbacksRef.current[key];
    if (typeof cb === 'function') {
      try {
        cb();
      } catch (err) {
        console.error(`countContext: fetch for "${key}" failed:`, err);
      }
    }
  }, []);

  // Debounce refetches so a burst of invalidations for the same key only
  // fires one HTTP request. Each key is fetched exactly once per flush.
  const flushPendingRefetches = useCallback(() => {
    refetchTimerRef.current = null;
    const keys = Array.from(pendingRefetchRef.current);
    pendingRefetchRef.current = new Set();
    for (const key of keys) {
      runRegisteredFetch(key);
    }
  }, [runRegisteredFetch]);

  const queueRefetch = useCallback(
    key => {
      if (!key) return;
      pendingRefetchRef.current.add(key);
      if (refetchTimerRef.current) return;
      refetchTimerRef.current = setTimeout(flushPendingRefetches, REFETCH_DEBOUNCE_MS);
    },
    [flushPendingRefetches]
  );

  const registerInterval = useCallback(
    (key, callback) => {
      if (!key || typeof callback !== 'function') return;

      fetchCallbacksRef.current[key] = callback;

      refCounters.current[key] = (refCounters.current[key] || 0) + 1;
      if (refCounters.current[key] === 1) {
        // first registration for this key: run once so the UI has a value
        // immediately, without waiting for the next server-driven invalidate
        try {
          callback();
        } catch (err) {
          console.error(`countContext: initial fetch for "${key}" failed:`, err);
        }
      }
    },
    []
  );

  const unregisterInterval = useCallback(key => {
    if (!key || !refCounters.current[key]) return;
    refCounters.current[key] -= 1;
    if (refCounters.current[key] <= 0) {
      delete refCounters.current[key];
      delete fetchCallbacksRef.current[key];
      pendingRefetchRef.current.delete(key);
    }
  }, []);

  const refreshCount = useCallback(
    key => {
      if (!key) return;
      runRegisteredFetch(key);
    },
    [runRegisteredFetch]
  );

  useEffect(() => {
    if (!authCtx.token) {
      refCounters.current = {};
      fetchCallbacksRef.current = {};
      pendingRefetchRef.current = new Set();
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
      setCounts({});
    }
  }, [authCtx.token]);

  useEffect(() => {
    const unsubscribe = subscribeCountsInvalidate(payload => {
      const keys =
        payload && Array.isArray(payload.keys) ? payload.keys.filter(k => typeof k === 'string') : [];
      for (const key of keys) {
        queueRefetch(key);
      }
    });
    return unsubscribe;
  }, [queueRefetch]);

  useEffect(() => {
    return () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      counts,
      setCount,
      registerInterval,
      unregisterInterval,
      refreshCount,
    }),
    [counts, setCount, registerInterval, unregisterInterval, refreshCount]
  );

  return <CountContext.Provider value={contextValue}>{children}</CountContext.Provider>;
};

export default CountContext;
